import { useCallback, useState } from 'react';
import useFetch, { ServiceParams, ServiceResponse } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { Requester } from '../models/RequesterModel';

export type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

const qs = (filters?: Record<string, any>) => {
  if (!filters) return '';
  return Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
};

/**
 * useRequestersService
 * - Funciones memoizadas para mantener identidad estable entre renders.
 * - Estado `fetching` centralizado por operación.
 */
const useRequestersService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFetch = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  // GET /api/v1/companies/requesters/?filters
  const list = useCallback(
    (filters?: Record<string, any>, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: `${EndPoints.REQUESTERS.LIST}${qs(filters) ? `?${qs(filters)}` : ''}`,
        setFetching: v => setFetch('list', v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  // GET /api/v1/companies/requesters/:id
  const show = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.REQUESTERS.SHOW.replace(':id', String(id)),
        setFetching: v => setFetch('show', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  // POST /api/v1/companies/requesters/store/
  const store = useCallback(
    (payload: Requester, events: Events = {}) =>
      doPost({
        url: EndPoints.REQUESTERS.STORE,
        body: payload,
        setFetching: v => setFetch('store', v),
        ...events,
      } as ServiceParams),
    [doPost, setFetch]
  );

  // PUT /api/v1/companies/requesters/:id/update/
  const update = useCallback(
    (id: number, payload: Requester, events: Events = {}) =>
      doPut({
        url: EndPoints.REQUESTERS.UPDATE.replace(':id', String(id)),
        body: payload,
        setFetching: v => setFetch('update', v),
        ...events,
      } as ServiceParams),
    [doPut, setFetch]
  );
  // DELETE /api/v1/companies/requesters/:id/delete/
  const remove = useCallback(
    (id: number, events: Events = {}) =>
      doDelete({
        url: EndPoints.REQUESTERS.DELETE.replace(':id', String(id)),
        setFetching: v => setFetch('delete', v),
        ...events,
      } as ServiceParams),
    [doDelete, setFetch]
  );


  const create = useCallback(
    (events: Events = {}) =>
      doGet({
        url: EndPoints.REQUESTERS.CREATE,
        setFetching: v => setFetch('create', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  
  const edit = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.REQUESTERS.EDIT.replace(':id', String(id)),
        setFetching: (v) => setFetch('edit', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );
  const byCompany = useCallback(
      (companyId: number, events?: Events, signal?: AbortSignal) =>
        doGet({ 
          url: EndPoints.REQUESTERS.BY_COMPANY.replace(':id', String(companyId)), 
          setFetching: v => setFetch('byCompany', v), 
          signal,
          ...events 
        } as ServiceParams),
      [doGet, setFetch]
    );


  return { fetching, list, show, store, update, remove, create, edit, byCompany };
};

export default useRequestersService;

